import React, {useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import AnimateHeight from 'react-animate-height'
import moment from 'moment'
import _ from 'lodash'

import AccommodationForm from '../Accomodation/AccommodationForm'
import BookingStepper from '../BookingStepper/BookerStepper'
import Calendar from '../Calender/Calender'

import { isRailBookers, phoneNumbers } from '../../configuration/brand'
import { getRAQFormUrl } from '../../configuration/deeplinking'
import labels from '../../configuration/labels'
import { getBrand } from '../../redux/reducers/configuration'
import {
  getTour,
  getTourAccommodationOptions,
  getTourBookingPath,
  getTourDetailsForSoftTrip,
} from '../../redux/reducers/tourDetails'
import { formatRoomDetailsForDeepLinking } from '../../utilities/content/tours'
import '../../styles/scss/elements/desktop-booking.scss'

const DEFAULT_ROOM = {
  adults: ['A', 'A'],
  children: [],
}

const bookingSteps = {
  details: 'tour-details',
  pricing: 'tour-pricing',
}

const DesktopBooking = ({onClose}) => {
  const accommodationOptions = useSelector(getTourAccommodationOptions)
  const brand = useSelector(getBrand)
  const modalHeader = useRef(null)
  const softTripBookingPath = useSelector(getTourBookingPath)
  const softTripDetails = useSelector(getTourDetailsForSoftTrip)
  const tour = useSelector(getTour)
  const [currentStep, setCurrentStep] = useState(bookingSteps.pricing)
  const [outgoingUrl, setOutgoingUrl] = useState(null)
  const [preAcc, setPreAcc] = useState('')
  const [postAcc, setPostAcc] = useState('')
  const [pricingPlan, setPricingPlan] = useState(null)
  const [rooms, setRooms] = useState([DEFAULT_ROOM])
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  function onSetCurrentStep(step) {
    setCurrentStep(step)
    if (step === bookingSteps.pricing) {
      setPreAcc('')
      setPostAcc('')
      setRooms([DEFAULT_ROOM])
    }
  }

  function addRoom() {
    if (_.size(rooms) < 4) {
      setRooms(_.concat(rooms, [DEFAULT_ROOM]))
    }
  }

  function removeRoom() {
    if (_.size(rooms) > 1) {
      setRooms(_.take(rooms, _.size(rooms) - 1))
    }
  }

  function updateRoom(room, index) {
    const updatedRooms = _.cloneDeep(rooms)
    updatedRooms[index] = room
    setRooms(updatedRooms)
  }

  function getSoftTripDeepLinkingURL(start, end) {
    const softTripDeepLinkURL = new URL(
      `${process.env.REACT_APP_BOOKING_BASE_URL}${softTripBookingPath}`
    )
    softTripDeepLinkURL.search = new URLSearchParams({
      ...softTripDetails,
      ...(preAcc && {
        Preacc: _.get(accommodationOptions, 'preAcc'),
        Preaccduration: preAcc,
      }),
      ...(postAcc && {
        Postacc: _.get(accommodationOptions, 'postAcc'),
        Postaccduration: postAcc,
      }),
      tbCheckIn: moment(start || startDate).format('YYYY-MM-DD'),
      tbCheckOut: moment(end || endDate).format('YYYY-MM-DD'),
      ...formatRoomDetailsForDeepLinking(rooms, tour.softripBooking),
      ItemNumber: pricingPlan,
      SelectedSeq: pricingPlan,
    })
    return softTripDeepLinkURL.toString()
  }

  function onSelectCalendarDate(startDate, endDate) {
    if (isRailBookers() || !tour.softripBooking) {
      setOutgoingUrl(getRAQFormUrl(false, startDate))
    }
    setStartDate(startDate)
    setEndDate(endDate)
  }

  function onSubmit() {
    if (currentStep === bookingSteps.pricing) {
      if (outgoingUrl) {
        window.location.href = outgoingUrl
      } else {
        setCurrentStep(bookingSteps.details)
        if (modalHeader.current) {
          modalHeader.current.scrollIntoView()
        }
      }
    }
    if (currentStep === bookingSteps.details) {
      window.location.href = getSoftTripDeepLinkingURL()
    }
  }

  return (
    <div
      className="u-full-width u-flex u-flex-column desktop-booking"
    >
      <div
        className="u-flex u-flex-align-center u-flex-space-between desktop-booking__header"
        ref={modalHeader}
      >
        <h5>{tour.name}</h5>
        <button
          aria-label={labels.close}
          className="u-flex u-flex-align-center"
          onClick={onClose}
        >
          {labels.close}
        </button>
      </div>
      <AnimateHeight height={isRailBookers() ? 0 : 'auto'}>
        <BookingStepper
          currentStep={currentStep}
          onSetCurrentStep={onSetCurrentStep}
        />
      </AnimateHeight>
      <div className="u-full-width u-flex u-flex-column u-flex-justify-center desktop-booking__content-wrapper">
        {currentStep === bookingSteps.pricing && (
          <Calendar
            onCalendarCallBack={onSelectCalendarDate}
            onSelectPricingPlan={setPricingPlan}
            selectedDate={startDate}
            submitButton={(
              <button
                aria-label={isRailBookers() ? labels.bookingRequestQuote : labels.bookingNextStep}
                className="u-flex u-flex-align-center desktop-booking__content-wrapper__footer__submit-button"
                disabled={!startDate}
                onClick={onSubmit}
              >
                {isRailBookers() || !tour.softripBooking ? labels.bookingRequestQuote : labels.bookingNextStep}
              </button>
            )}
            tourStartDate={startDate}
          />
        )}
        {currentStep === bookingSteps.details && (
          <div className="desktop-booking__content-wrapper__passenger-details">
            <h3>{labels.passengerInfo}</h3>
            <AccommodationForm
              accommodationOptions={accommodationOptions}
              rooms={rooms}
              addRoom={addRoom}
              removeRoom={removeRoom}
              updateRoom={updateRoom}
              preAcc={preAcc}
              setPreAcc={setPreAcc}
              postAcc={postAcc}
              setPostAcc={setPostAcc}
            />
          </div>
        )}
        <div className="u-full-width u-flex u-flex-space-between u-flex-align-end desktop-booking__content-wrapper__footer">
          <div>
            {labels.bookingPrompt}
            <span>{_.get(phoneNumbers, `[${brand}]`)}</span>
          </div>
          <button
            aria-label={isRailBookers() ? labels.bookingRequestQuote : labels.bookingNextStep}
            className="u-flex u-flex-align-center desktop-booking__content-wrapper__footer__submit-button"
            disabled={!startDate}
            onClick={onSubmit}
          >
            {isRailBookers() || !tour.softripBooking ? labels.bookingRequestQuote : labels.bookingNextStep}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DesktopBooking
